import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useInitSyncOAuth, useConnectApple } from '../hooks/useSyncConnections';

interface ConnectProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected?: () => void;
}

type View = 'choose' | 'apple';

export function ConnectProviderDialog({
  open,
  onOpenChange,
  onConnected,
}: ConnectProviderDialogProps) {
  const [view, setView] = useState<View>('choose');
  const [appleEmail, setAppleEmail] = useState('');
  const [applePassword, setApplePassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const initOAuth = useInitSyncOAuth();
  const connectApple = useConnectApple();

  const handleOAuth = async (provider: 'google' | 'microsoft') => {
    setError(null);
    try {
      await initOAuth.mutateAsync(provider);
      onOpenChange(false);
      // Deliberately NOT onConnected(): this fires when the popup is LAUNCHED,
      // not when the user finishes (or abandons) the provider's consent
      // screen. OAuth signals real completion through the SYNC_OAUTH_CHANNEL
      // broadcast from the callback page. Calling it here popped the calendar
      // picker open behind the OAuth window for an unrelated connection, and
      // left the "open a picker when one appears" flag armed indefinitely if
      // the user cancelled.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start OAuth flow');
    }
  };

  const handleAppleSubmit = async () => {
    if (!appleEmail.trim() || !applePassword.trim()) return;

    setError(null);
    try {
      await connectApple.mutateAsync({
        username: appleEmail.trim(),
        appPassword: applePassword.trim(),
      });
      resetState();
      onOpenChange(false);
      onConnected?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect Apple account');
    }
  };

  const resetState = () => {
    setView('choose');
    setAppleEmail('');
    setApplePassword('');
    setError(null);
  };

  const isLoading = initOAuth.isPending || connectApple.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !isLoading) {
          resetState();
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {view === 'choose' ? 'Connect External Calendar' : 'Connect Apple Calendar'}
          </DialogTitle>
        </DialogHeader>

        {view === 'choose' && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Choose a provider to sync calendars from.
            </p>

            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-12"
              onClick={() => handleOAuth('google')}
              disabled={isLoading}
            >
              {initOAuth.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleIcon />}
              Google Calendar
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-12"
              onClick={() => setView('apple')}
              disabled={isLoading}
            >
              <AppleIcon />
              Apple Calendar
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-12"
              onClick={() => handleOAuth('microsoft')}
              disabled={isLoading}
            >
              {initOAuth.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <MicrosoftIcon />
              )}
              Microsoft Outlook
            </Button>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        {view === 'apple' && (
          <div className="space-y-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 -ml-2 mb-1"
              onClick={() => {
                setError(null);
                setView('choose');
              }}
              disabled={connectApple.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>

            <div className="space-y-2">
              <Label htmlFor="apple-email">Apple ID Email</Label>
              <Input
                id="apple-email"
                type="email"
                placeholder="you@icloud.com"
                value={appleEmail}
                onChange={(e) => setAppleEmail(e.target.value)}
                disabled={connectApple.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="apple-password">App-Specific Password</Label>
              <Input
                id="apple-password"
                type="password"
                placeholder="xxxx-xxxx-xxxx-xxxx"
                value={applePassword}
                onChange={(e) => setApplePassword(e.target.value)}
                disabled={connectApple.isPending}
              />
              <p className="text-xs text-muted-foreground">
                Generate an app-specific password at{' '}
                <a
                  href="https://appleid.apple.com/account/manage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  appleid.apple.com
                </a>
              </p>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  resetState();
                  onOpenChange(false);
                }}
                disabled={connectApple.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAppleSubmit}
                disabled={!appleEmail.trim() || !applePassword.trim() || connectApple.isPending}
              >
                {connectApple.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Connect
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Provider icons (simple inline SVGs) ── */

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 21 21" className="h-5 w-5" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}
